import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import {
  fetchManagerDaily,
  fetchManagerClientFrequency,
  fetchManagerLoad,
  fetchManagerSummary,
  fetchManagerAuthors,
} from "../api";
import Loading from "../components/Loading";
import ErrorRetry from "../components/ErrorRetry";

const WEEKDAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseISO(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function moscowTodayISO() {
  // survey_date в БД пишется по московской дате (как в боте) — «сегодня»
  // на дашборде должно совпадать с ней, а не с часовым поясом браузера.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Moscow",
  }).format(new Date());
}

function startOfToday() {
  return parseISO(moscowTodayISO());
}

function mondayOf(d) {
  const r = new Date(d);
  const wd = (r.getDay() + 6) % 7; // 0 = понедельник
  return addDays(r, -wd);
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = iso instanceof Date ? iso : parseISO(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateShort(d, { withYear = false } = {}) {
  return d.toLocaleDateString(
    "ru-RU",
    withYear
      ? { day: "2-digit", month: "2-digit", year: "2-digit" }
      : { day: "2-digit", month: "2-digit" }
  );
}

function formatDateWithWeekday(iso) {
  const d = parseISO(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const weekday = d.toLocaleDateString("ru-RU", { weekday: "short" });
  return `${weekday}, ${formatDate(d)}`;
}

function formatReminderTime(hour, minute) {
  const h = hour != null ? hour : 18;
  const m = minute != null ? minute : 0;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function presetRange(key) {
  const t = startOfToday();
  if (key === "7") return [toISO(addDays(t, -6)), toISO(t)];
  if (key === "30") return [toISO(addDays(t, -29)), toISO(t)];
  if (key === "month")
    return [toISO(new Date(t.getFullYear(), t.getMonth(), 1)), toISO(t)];
  return ["", ""];
}

const PRESETS = [
  { key: "7", label: "7 дней" },
  { key: "30", label: "30 дней" },
  { key: "month", label: "Этот месяц" },
  { key: "all", label: "Всё" },
];

function disciplineColor(pct) {
  if (pct == null) return "var(--text-muted)";
  if (pct >= 80) return "var(--score-high)";
  if (pct >= 50) return "var(--text-primary)";
  return "var(--score-low)";
}

/** Высокая загрузка — сигнал внимания (не «хорошо»). */
function workloadColor(score) {
  if (score == null) return undefined;
  if (score >= 4) return "var(--score-low)";
  if (score <= 2) return "var(--score-high)";
  return "var(--text-primary)";
}

/** Низкая энергия — сигнал внимания. */
function energyColor(score) {
  if (score == null) return undefined;
  if (score <= 2) return "var(--score-low)";
  if (score >= 4) return "var(--score-high)";
  return "var(--text-primary)";
}

function MetricCard({ label, value, valueColor, sub }) {
  return (
    <div className="metric-card">
      <div className="metric-label">{label}</div>
      <div
        className="metric-value"
        style={{ color: valueColor || "var(--text-primary)" }}
      >
        {value}
      </div>
      {sub && <div className="mgr-metric-sub">{sub}</div>}
    </div>
  );
}

function DayScores({ row }) {
  if (row.score_workload == null && row.score_energy == null) return null;
  return (
    <div className="mgr-day-scores">
      <span>
        Загрузка:{" "}
        <strong style={{ color: workloadColor(row.score_workload) || "var(--text-primary)" }}>
          {row.score_workload != null ? `${row.score_workload}/5` : "—"}
        </strong>
      </span>
      <span>
        Энергия:{" "}
        <strong style={{ color: energyColor(row.score_energy) || "var(--text-primary)" }}>
          {row.score_energy != null ? `${row.score_energy}/5` : "—"}
        </strong>
      </span>
    </div>
  );
}

function heatLevel(count) {
  if (count >= 5) return "heat-3";
  if (count >= 3) return "heat-2";
  if (count >= 1) return "heat-1";
  return "heat-0";
}

function buildTodayStatus(rows, todayIso, authors, authorId) {
  const expected = authorId
    ? authors.filter((a) => String(a.id) === String(authorId))
    : authors;
  const todayRows = (rows || []).filter((r) => r.survey_date === todayIso);
  const completedRows = todayRows.filter((r) => r.completed_at);
  const draftRows = todayRows.filter((r) => !r.completed_at);
  const completedIds = new Set(completedRows.map((r) => r.employee_id));
  const total =
    expected.length > 0
      ? expected.length
      : new Set(todayRows.map((r) => r.employee_id)).size;
  const pendingNames = expected
    .filter((a) => !completedIds.has(a.id))
    .map((a) => a.full_name);
  return {
    total,
    completed: completedRows.length,
    drafts: draftRows.length,
    pendingNames,
    clientCount: completedRows.reduce((sum, r) => sum + (r.client_count || 0), 0),
  };
}

function TodayBanner({ status, todayIsWorkday, reminderHour, reminderMinute }) {
  // undefined — статус ещё загружается, ничего не показываем.
  if (status === undefined) return null;

  const { total, completed, drafts, pendingNames, clientCount } = status;
  const multi = total > 1;

  if (total > 0 && completed >= total) {
    return (
      <div className="mgr-today-banner mgr-today-banner--done">
        <span className="mgr-today-dot" />
        {multi
          ? `Сегодня сдали все (${completed}/${total})`
          : "Отчёт за сегодня заполнен"}
        {clientCount > 0 && <> · {clientCount} кл.</>}
      </div>
    );
  }

  if (completed > 0 && multi) {
    const missing =
      pendingNames.length > 0 ? ` · нет: ${pendingNames.join(", ")}` : "";
    return (
      <div className="mgr-today-banner mgr-today-banner--draft">
        <span className="mgr-today-dot" />
        Сегодня сдали {completed}/{total}
        {missing}
      </div>
    );
  }

  if (drafts > 0 && completed === 0) {
    return (
      <div className="mgr-today-banner mgr-today-banner--draft">
        <span className="mgr-today-dot" />
        {multi
          ? `Черновики за сегодня (${drafts}) — допишите отчёт в боте`
          : "Черновик за сегодня — допишите отчёт в боте"}
      </div>
    );
  }

  // Выходные и праздники: бот не напоминает (is_workday на бэке).
  // Показываем pending только когда бэк явно сказал «сегодня рабочий».
  if (todayIsWorkday !== true) return null;

  const time = formatReminderTime(reminderHour, reminderMinute);
  return (
    <div className="mgr-today-banner mgr-today-banner--pending">
      <span className="mgr-today-dot" />
      {multi
        ? `Сегодня ещё никто не сдал (0/${total}) — бот напомнит в ${time}`
        : `Отчёт за сегодня ещё не заполнен — бот напомнит в ${time}`}
    </div>
  );
}

function DayContent({ row }) {
  const notesByClient = useMemo(
    () =>
      new Map(
        (row.client_tasks || []).map((t) => [t.client_name, t.tasks_text])
      ),
    [row.client_tasks]
  );
  const hasGeneral = row.extra_tasks_text && row.extra_tasks_text.trim();
  const clients = row.clients || [];

  if (clients.length === 0 && !hasGeneral) {
    return (
      <div>
        <DayScores row={row} />
        <div className="mgr-empty-hint">Подробностей нет.</div>
      </div>
    );
  }

  return (
    <div className="mgr-day-content">
      <DayScores row={row} />
      {clients.map((name) => {
        const text = notesByClient.get(name);
        const hasText = text && text.trim();
        return (
          <div key={name} className="mgr-day-entry">
            <div className="mgr-day-entry-name">{name}</div>
            <div className={`mgr-day-entry-text${hasText ? "" : " muted"}`}>
              {hasText ? text : "без заметок"}
            </div>
          </div>
        );
      })}
      {hasGeneral && (
        <div className="mgr-day-entry mgr-day-entry--general">
          <div className="mgr-day-entry-name">Общие задачи</div>
          <div className="mgr-day-entry-text">{row.extra_tasks_text}</div>
        </div>
      )}
    </div>
  );
}

function DayDetails({ rows, onClose }) {
  const list = Array.isArray(rows) ? rows : rows ? [rows] : [];
  if (list.length === 0) return null;
  const surveyDate = list[0].survey_date;
  const completed = list.filter((r) => r.completed_at).length;
  const clientCount = list.reduce((sum, r) => sum + (r.client_count || 0), 0);
  const allDraft = completed === 0;
  return (
    <div className="mgr-day-details animate-fade-in">
      <div className="mgr-day-details-head">
        <strong>{formatDate(surveyDate)}</strong>
        {allDraft && <span className="mgr-badge-draft">черновик</span>}
        {list.length > 1 && (
          <span className="mgr-day-details-count">
            {completed}/{list.length} сдали
          </span>
        )}
        <span className="mgr-day-details-count">
          {clientCount > 0 ? `${clientCount} кл.` : "без клиентов"}
        </span>
        <button
          type="button"
          className="mgr-day-details-close"
          onClick={onClose}
          title="Закрыть"
          aria-label="Закрыть"
        >
          ×
        </button>
      </div>
      {list.map((row) => (
        <div key={row.id} className="mgr-day-author-block">
          {list.length > 1 && (
            <div className="mgr-day-author-name">
              {row.employee_name || "—"}
              {!row.completed_at && (
                <span className="mgr-badge-draft">черновик</span>
              )}
            </div>
          )}
          <DayContent row={row} />
        </div>
      ))}
    </div>
  );
}

const JOURNAL_PREVIEW_CHIPS = 4;

function JournalDay({ row, expanded, onToggle }) {
  const completed = Boolean(row.completed_at);
  const clients = row.clients || [];
  const hasNotes =
    (row.client_tasks || []).some((t) => t.tasks_text && t.tasks_text.trim()) ||
    (row.extra_tasks_text && row.extra_tasks_text.trim());
  const preview = clients.slice(0, JOURNAL_PREVIEW_CHIPS);
  const more = clients.length - preview.length;

  return (
    <div className={`mgr-journal-day${expanded ? " expanded" : ""}${completed ? "" : " draft"}`}>
      <button
        type="button"
        className="mgr-journal-row"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span
          className={`mgr-journal-status ${completed ? "done" : "draft"}`}
          title={completed ? "Заполнен" : "Черновик"}
        />
        <span className="mgr-journal-date">
          {formatDateWithWeekday(row.survey_date)}
        </span>
        {row.employee_name && (
          <span className="mgr-journal-author" title="Автор отчёта">
            {row.employee_name}
          </span>
        )}
        <span className="mgr-journal-count">
          {row.client_count > 0 ? `${row.client_count} кл.` : "—"}
        </span>
        {(row.score_workload != null || row.score_energy != null) && (
          <span
            className="mgr-journal-scores"
            title="Оценки за день: загрузка / энергия"
          >
            загрузка{" "}
            <strong style={{ color: workloadColor(row.score_workload) }}>
              {row.score_workload ?? "—"}
            </strong>
            {" · энергия "}
            <strong style={{ color: energyColor(row.score_energy) }}>
              {row.score_energy ?? "—"}
            </strong>
          </span>
        )}
        <span className="mgr-journal-preview">
          {preview.map((c) => (
            <span key={c} className="mgr-chip">
              {c}
            </span>
          ))}
          {more > 0 && <span className="mgr-chip mgr-chip--more">+{more}</span>}
          {!completed && clients.length === 0 && (
            <span className="mgr-journal-draft-note">черновик</span>
          )}
        </span>
        {hasNotes && (
          <svg
            className="mgr-journal-notes"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-label="Есть заметки"
          >
            <path d="M12 20h9"></path>
            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
          </svg>
        )}
        <svg
          className="mgr-journal-chevron"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {expanded && (
        <div className="mgr-journal-body animate-fade-in">
          <DayContent row={row} />
        </div>
      )}
    </div>
  );
}

function ActivityCalendar({
  daily,
  dateFrom,
  dateTo,
  holidays,
  selectedDay,
  onSelectDay,
  expectedAuthors = 1,
}) {
  // Все отчёты по датам — список авторов за день.
  const dayMap = useMemo(() => {
    const m = new Map();
    daily.forEach((row) => {
      const list = m.get(row.survey_date) || [];
      list.push(row);
      m.set(row.survey_date, list);
    });
    m.forEach((list, key) => {
      list.sort((a, b) =>
        String(a.employee_name || "").localeCompare(String(b.employee_name || ""), "ru")
      );
      m.set(key, list);
    });
    return m;
  }, [daily]);

  const holidaySet = useMemo(() => new Set(holidays || []), [holidays]);

  const { weeks, hasData } = useMemo(() => {
    const today = startOfToday();
    let start;
    if (dateFrom) {
      start = parseISO(dateFrom);
    } else {
      const dates = daily.map((r) => parseISO(r.survey_date));
      start = dates.length
        ? new Date(Math.min(...dates.map((d) => d.getTime())))
        : addDays(today, -29);
    }
    const end = dateTo ? parseISO(dateTo) : today;
    if (end < start) return { weeks: [], hasData: false };

    const gridStart = mondayOf(start);
    const result = [];
    let cur = gridStart;
    let guard = 0;
    while (cur <= end && guard < 400) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(cur));
        cur = addDays(cur, 1);
      }
      result.push({ start: week[0], days: week });
      guard += 1;
    }
    return { weeks: result, hasData: daily.some((r) => r.completed_at) };
  }, [daily, dateFrom, dateTo]);

  if (weeks.length === 0) {
    return (
      <div style={{ padding: "24px", color: "var(--text-muted)" }}>
        Нет данных за выбранный период.
      </div>
    );
  }

  const today = startOfToday();
  const rangeStart = dateFrom
    ? parseISO(dateFrom)
    : weeks.length
    ? weeks[0].days[0]
    : today;
  const rangeEnd = dateTo ? parseISO(dateTo) : today;

  return (
    <div>
      <div className="mgr-cal">
        <div className="mgr-cal-months">
          <div className="mgr-cal-weekday-spacer" />
          {weeks.map((w, i) => {
            const prev = i > 0 ? weeks[i - 1].start.getMonth() : null;
            const showLabel = i === 0 || w.start.getMonth() !== prev;
            return (
              <div key={i} className="mgr-cal-month-label">
                {showLabel
                  ? w.start.toLocaleDateString("ru-RU", { month: "short" })
                  : ""}
              </div>
            );
          })}
        </div>
        <div className="mgr-cal-body">
          <div className="mgr-cal-weekdays">
            {WEEKDAY_LABELS.map((l) => (
              <div key={l} className="mgr-cal-weekday">
                {l}
              </div>
            ))}
          </div>
          <div className="mgr-cal-weeks">
            {weeks.map((w, wi) => (
              <div key={wi} className="mgr-cal-week">
                {w.days.map((day, di) => {
                  const inRange = day >= rangeStart && day <= rangeEnd;
                  const isFuture = day > today;
                  const isToday = day.getTime() === today.getTime();
                  const iso = toISO(day);
                  const reports = dayMap.get(iso) || [];
                  const completedReports = reports.filter((r) => r.completed_at);
                  const isDraft =
                    reports.length > 0 && completedReports.length === 0;
                  const clientCount = completedReports.reduce(
                    (sum, r) => sum + (r.client_count || 0),
                    0
                  );
                  const expected = Math.max(expectedAuthors || 1, reports.length);
                  const isWeekend = di >= 5;

                  let cls = "mgr-cal-cell";
                  let title = formatDate(day);
                  if (!inRange || isFuture) {
                    cls += " empty";
                    title = "";
                  } else if (completedReports.length > 0) {
                    cls += ` ${heatLevel(clientCount)}`;
                    const authors = completedReports
                      .map((r) => r.employee_name)
                      .filter(Boolean)
                      .join(", ");
                    title =
                      expected > 1
                        ? `${formatDate(day)} · сдали ${completedReports.length}/${expected} · ${clientCount} кл.${
                            authors ? ` · ${authors}` : ""
                          }`
                        : `${formatDate(day)} · ${clientCount} кл.${
                            authors ? ` · ${authors}` : ""
                          }`;
                  } else if (isDraft) {
                    cls += " draft";
                    title = `${formatDate(day)} · черновик — отчёт начат, но не завершён`;
                  } else if (holidaySet.has(iso)) {
                    cls += " holiday";
                    title = `${formatDate(day)} · праздничный день`;
                  } else if (isWeekend) {
                    cls += " weekend";
                    title = `${formatDate(day)} · выходной`;
                  } else if (isToday) {
                    // Отчёт заполняется вечером — до этого сегодняшний день
                    // ещё не «пропущен».
                    cls += " pending";
                    title = `${formatDate(day)} · отчёт ещё не заполнен`;
                  } else {
                    cls += " missed";
                    title = `${formatDate(day)} · отчёт не заполнен`;
                  }
                  if (reports.length > 0) {
                    const isSelected = selectedDay === iso;
                    return (
                      <button
                        key={di}
                        type="button"
                        className={`${cls} clickable${isSelected ? " selected" : ""}`}
                        title={title || undefined}
                        aria-label={title || undefined}
                        onClick={() => onSelectDay(isSelected ? null : iso)}
                      />
                    );
                  }
                  return (
                    <div
                      key={di}
                      className={cls}
                      title={title || undefined}
                      aria-label={title || undefined}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mgr-cal-legend">
        <div className="mgr-cal-legend-group">
          <span>меньше</span>
          <span className="mgr-cal-cell heat-0" />
          <span className="mgr-cal-cell heat-1" />
          <span className="mgr-cal-cell heat-2" />
          <span className="mgr-cal-cell heat-3" />
          <span>больше клиентов</span>
        </div>
        <div className="mgr-cal-legend-group">
          <span className="mgr-cal-cell missed" />
          <span>рабочий день без отчёта</span>
        </div>
        <div className="mgr-cal-legend-group">
          <span className="mgr-cal-cell draft" />
          <span>черновик</span>
        </div>
        {holidaySet.size > 0 && (
          <div className="mgr-cal-legend-group">
            <span className="mgr-cal-cell holiday" />
            <span>праздник</span>
          </div>
        )}
        {hasData && (
          <div className="mgr-cal-legend-group">
            <span>нажмите на день с отчётом, чтобы посмотреть детали</span>
          </div>
        )}
      </div>
      {selectedDay && dayMap.get(selectedDay) && (
        <DayDetails
          rows={dayMap.get(selectedDay)}
          onClose={() => onSelectDay(null)}
        />
      )}
      {!hasData && (
        <div style={{ marginTop: "12px", color: "var(--text-muted)", fontSize: "13px" }}>
          Заполненных отчётов пока нет.
        </div>
      )}
    </div>
  );
}

// Дольше этого диапазона пропущенные дни не дозаполняем — график и так плотный.
const LOAD_FILL_MAX_DAYS = 400;
const LOAD_BAR_FILL = "var(--accent-primary)";
const LOAD_BAR_MISSING = "color-mix(in srgb, var(--text-muted) 35%, transparent)";

function LoadChart({ load, dateFrom, dateTo, holidays }) {
  const data = useMemo(() => {
    // Дозаполняем пропущенные рабочие дни нулями, чтобы ось была непрерывной
    // и провалы в отчётности были видны, а не схлопывались.
    // Сегодняшний день без отчёта не считаем пропуском (отчёт сдаётся вечером).
    const byDate = new Map(load.map((item) => [item.survey_date, item]));
    const holidaySet = new Set(holidays || []);
    const today = startOfToday();
    const todayIso = toISO(today);
    const start = dateFrom
      ? parseISO(dateFrom)
      : load.length
      ? parseISO(load[0].survey_date)
      : null;
    let end = dateTo ? parseISO(dateTo) : today;
    if (end > today) end = today;

    const spanDays = start ? (end - start) / 86400000 : Infinity;
    const withYear = spanDays > 365;
    if (!start || spanDays < 0 || spanDays > LOAD_FILL_MAX_DAYS) {
      return load.map((item) => ({
        ...item,
        label: formatDateShort(parseISO(item.survey_date), { withYear }),
      }));
    }

    const rows = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      const iso = toISO(d);
      const item = byDate.get(iso);
      const isWorkday = (d.getDay() + 6) % 7 < 5 && !holidaySet.has(iso);
      if (item) {
        rows.push({
          ...item,
          label: formatDateShort(d, { withYear }),
        });
      } else if (isWorkday && iso !== todayIso) {
        rows.push({
          survey_date: iso,
          client_count: 0,
          missing: true,
          label: formatDateShort(d, { withYear }),
        });
      }
    }
    return rows;
  }, [load, dateFrom, dateTo, holidays]);
  const avg = useMemo(() => {
    if (load.length === 0) return null;
    return (
      Math.round(
        (load.reduce((s, item) => s + item.client_count, 0) / load.length) * 10
      ) / 10
    );
  }, [load]);

  if (load.length < 2) {
    return (
      <div className="mgr-empty-hint mgr-empty-hint--padded">
        Недостаточно данных для графика — нужно хотя бы два заполненных отчёта.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 10, right: 16, left: -20, bottom: 0 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="var(--border-color)"
          vertical={false}
        />
        <XAxis
          dataKey="label"
          stroke="var(--text-muted)"
          fontSize={12}
          tickLine={false}
          axisLine={{ stroke: "var(--border-color)" }}
        />
        <YAxis
          allowDecimals={false}
          stroke="var(--text-muted)"
          fontSize={12}
          tickLine={false}
          axisLine={{ stroke: "var(--border-color)" }}
        />
        <Tooltip
          cursor={{ fill: "color-mix(in srgb, var(--accent-primary) 8%, transparent)" }}
          contentStyle={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-color)",
            borderRadius: "8px",
            fontSize: "13px",
          }}
          labelFormatter={(label, payload) =>
            payload?.[0] ? formatDate(payload[0].payload.survey_date) : label
          }
          formatter={(value, _name, props) =>
            props?.payload?.missing
              ? ["нет отчёта", "День"]
              : [`${value} кл.`, "Клиентов"]
          }
        />
        {avg != null && (
          <ReferenceLine
            y={avg}
            stroke="var(--text-muted)"
            strokeDasharray="4 4"
            label={{
              value: `среднее ${avg}`,
              position: "insideTopRight",
              fill: "var(--text-muted)",
              fontSize: 12,
            }}
          />
        )}
        <Bar dataKey="client_count" radius={[4, 4, 0, 0]} maxBarSize={28}>
          {data.map((entry, index) => (
            <Cell
              key={`cell-${entry.survey_date}-${index}`}
              fill={entry.missing ? LOAD_BAR_MISSING : LOAD_BAR_FILL}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function ClientBreakdown({ frequency }) {
  const [showAll, setShowAll] = useState(false);
  const total = frequency.reduce((s, f) => s + f.engagement_count, 0);
  const max = frequency.length ? frequency[0].engagement_count : 0;
  const visible = showAll ? frequency : frequency.slice(0, 8);

  if (frequency.length === 0) {
    return (
      <div className="mgr-empty-hint mgr-empty-hint--padded">
        Пока нет данных. Отчёты появятся после первого заполнения.
      </div>
    );
  }

  return (
    <div className="mgr-breakdown">
      {visible.map((f) => {
        const pct = total ? Math.round((f.engagement_count / total) * 100) : 0;
        const barWidth = max ? (f.engagement_count / max) * 100 : 0;
        return (
          <div key={f.client_profile_id} className="mgr-breakdown-row">
            <div className="mgr-breakdown-name" title={f.client_name}>
              {f.client_name}
            </div>
            <div className="mgr-breakdown-bar-wrap">
              <div
                className="mgr-breakdown-bar"
                style={{ width: `${Math.max(barWidth, 3)}%` }}
              />
            </div>
            <div className="mgr-breakdown-count">
              {f.engagement_count} дн.
              <span className="mgr-breakdown-pct">{pct}%</span>
            </div>
            <div className="mgr-breakdown-last">
              {f.last_engagement_date
                ? `посл.: ${formatDate(f.last_engagement_date)}`
                : "—"}
            </div>
          </div>
        );
      })}
      {frequency.length > 8 && (
        <button
          className="btn btn-sm"
          style={{ marginTop: "12px" }}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? "Свернуть" : `Показать всех (${frequency.length})`}
        </button>
      )}
    </div>
  );
}

export default function ManagerDashboard() {
  const initialRange = useMemo(() => presetRange("30"), []);
  const [daily, setDaily] = useState([]);
  const [frequency, setFrequency] = useState([]);
  const [load, setLoad] = useState([]);
  const [summary, setSummary] = useState(null);
  // Статус отчётов за сегодня — независим от фильтра дат.
  const [todayStatus, setTodayStatus] = useState(undefined);
  const [selectedDay, setSelectedDay] = useState(null);
  const [expandedDays, setExpandedDays] = useState(() => new Set());
  const [dateFrom, setDateFrom] = useState(initialRange[0]);
  const [dateTo, setDateTo] = useState(initialRange[1]);
  const [preset, setPreset] = useState("30");
  const [authorId, setAuthorId] = useState("");
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Счётчик актуальности: при быстрой смене периода медленный старый ответ
  // не должен перезаписать данные более нового запроса.
  const requestIdRef = useRef(0);

  useEffect(() => {
    fetchManagerAuthors()
      .then(setAuthors)
      .catch(() => setAuthors([]));
  }, []);

  const loadData = useCallback(() => {
    setError(null);
    setLoading(true);
    setSelectedDay(null);
    setExpandedDays(new Set());
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (authorId) params.employee_id = Number(authorId);

    const requestId = ++requestIdRef.current;
    const todayIso = toISO(startOfToday());
    const rangeCoversToday =
      (!dateFrom || dateFrom <= todayIso) && (!dateTo || dateTo >= todayIso);

    const todayParams = { date_from: todayIso, date_to: todayIso };
    if (authorId) todayParams.employee_id = Number(authorId);

    const requests = [
      fetchManagerDaily(params),
      fetchManagerClientFrequency(params),
      fetchManagerSummary(params),
      fetchManagerLoad(params),
    ];
    if (!rangeCoversToday) {
      requests.push(fetchManagerDaily(todayParams));
    }

    Promise.all(requests)
      .then((results) => {
        if (requestId !== requestIdRef.current) return;
        const [dailyData, freqData, summaryData, loadData_] = results;
        setDaily(dailyData);
        setFrequency(freqData);
        setSummary(summaryData);
        setLoad(loadData_);
        const todayRows = rangeCoversToday
          ? dailyData
          : results[4] || [];
        setTodayStatus(
          buildTodayStatus(todayRows, todayIso, authors, authorId)
        );
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load manager dashboard:", err);
        setError("Не удалось загрузить ежедневные отчёты");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [dateFrom, dateTo, authorId, authors]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const applyPreset = (key) => {
    setPreset(key);
    const [from, to] = presetRange(key === "all" ? "all" : key);
    setDateFrom(from);
    setDateTo(to);
  };

  const onDateChange = (setter) => (e) => {
    setPreset("custom");
    setter(e.target.value);
  };

  const toggleJournalDay = (id) =>
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  if (error) return <ErrorRetry message={error} onRetry={loadData} />;
  if (loading && !summary)
    return <Loading text="Загрузка данных руководителя..." />;

  const rangeCaption =
    summary && summary.range_start
      ? `${formatDate(summary.range_start)} – ${formatDate(summary.range_end)}`
      : "за всё время";
  const isRefreshing = loading && Boolean(summary);

  return (
    <div
      className={`manager-dashboard animate-fade-in${
        isRefreshing ? " is-refreshing" : ""
      }`}
    >
      <div className="section-header">
        <div>
          <h2>Ежедневные отчёты</h2>
          <div className="section-subtitle">
            Ежедневные отчёты руководителя и выбранных сотрудников: загрузка и клиенты
          </div>
        </div>
        <div className="controls" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          {isRefreshing && (
            <span className="mgr-refresh-hint" aria-live="polite">
              Обновление…
            </span>
          )}
          <label style={{ fontSize: "13px", color: "var(--text-muted)" }}>
            Сотрудник{" "}
            <select
              value={authorId}
              onChange={(e) => setAuthorId(e.target.value)}
              disabled={isRefreshing}
            >
              <option value="">Все</option>
              {authors.map((a) => (
                <option key={a.id} value={String(a.id)}>
                  {a.full_name}
                  {a.is_manager ? " (руководитель)" : ""}
                </option>
              ))}
            </select>
          </label>
          <div className="toolbar-segment">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                className={preset === p.key ? "active" : ""}
                onClick={() => applyPreset(p.key)}
                disabled={isRefreshing}
              >
                {p.label}
              </button>
            ))}
          </div>
          <label style={{ fontSize: "13px", color: "var(--text-muted)" }}>
            с{" "}
            <input
              type="date"
              value={dateFrom}
              onChange={onDateChange(setDateFrom)}
              disabled={isRefreshing}
            />
          </label>
          <label style={{ fontSize: "13px", color: "var(--text-muted)" }}>
            по{" "}
            <input
              type="date"
              value={dateTo}
              onChange={onDateChange(setDateTo)}
              disabled={isRefreshing}
            />
          </label>
        </div>
      </div>

      <TodayBanner
        status={todayStatus}
        todayIsWorkday={summary?.today_is_workday}
        reminderHour={summary?.reminder_hour}
        reminderMinute={summary?.reminder_minute}
      />

      {summary && (
        <div className="metrics-grid">
          <MetricCard
            label="Дисциплина"
            value={summary.discipline_pct != null ? `${summary.discipline_pct}%` : "—"}
            valueColor={disciplineColor(summary.discipline_pct)}
            sub={
              (summary.participants_count || 1) > 1
                ? `${summary.reported_workdays}/${summary.workdays} человеко-дней`
                : `${summary.reported_workdays}/${summary.workdays} раб. дней`
            }
          />
          <MetricCard
            label="Пропущено"
            value={summary.missed_workdays}
            valueColor={
              summary.missed_workdays > 0 ? "var(--score-low)" : "var(--score-high)"
            }
            sub={
              (summary.participants_count || 1) > 1
                ? "человеко-дней без отчёта"
                : "раб. дней без отчёта"
            }
          />
          <MetricCard
            label="Клиентов в день"
            value={summary.avg_clients_per_day}
            sub="в среднем"
          />
          <MetricCard
            label="Загрузка"
            value={summary.avg_workload != null ? `${summary.avg_workload}/5` : "—"}
            valueColor={workloadColor(summary.avg_workload)}
            sub="средняя за период"
          />
          <MetricCard
            label="Энергия"
            value={summary.avg_energy != null ? `${summary.avg_energy}/5` : "—"}
            valueColor={energyColor(summary.avg_energy)}
            sub="средняя за период"
          />
          <MetricCard
            label="Уникальных клиентов"
            value={summary.unique_clients}
            sub={
              summary.top_client_name
                ? `топ: ${summary.top_client_name} (${summary.top_client_count} дн.)`
                : "за период"
            }
          />
        </div>
      )}

      <div className="data-card">
        <div className="data-card-header">
          <div className="data-card-title">Активность по дням</div>
          <span className="mgr-card-caption">{rangeCaption}</span>
        </div>
        <div className="data-card-body">
          <ActivityCalendar
            daily={daily}
            dateFrom={dateFrom}
            dateTo={dateTo}
            holidays={summary?.holidays}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
            expectedAuthors={
              authorId
                ? 1
                : authors.length || summary?.participants_count || 1
            }
          />
        </div>
      </div>

      <div className="data-card" style={{ marginTop: "32px" }}>
        <div className="data-card-header">
          <div className="data-card-title">Динамика загрузки</div>
          <span className="mgr-card-caption">клиентов в день</span>
        </div>
        <div className="data-card-body">
          <LoadChart
            load={load}
            dateFrom={dateFrom}
            dateTo={dateTo}
            holidays={summary?.holidays}
          />
        </div>
      </div>

      <div className="data-card" style={{ marginTop: "32px" }}>
        <div className="data-card-header">
          <div className="data-card-title">Включение по клиентам</div>
          <span className="mgr-card-caption">{frequency.length} клиентов</span>
        </div>
        <div className="data-card-body">
          <ClientBreakdown frequency={frequency} />
        </div>
      </div>

      <div className="data-card" style={{ marginTop: "32px" }}>
        <div className="data-card-header">
          <div className="data-card-title">Журнал по дням</div>
          <span className="mgr-card-caption">{daily.length} записей</span>
        </div>
        <div className="data-card-body" style={{ padding: 0 }}>
          {daily.length === 0 ? (
            <div className="mgr-empty-hint mgr-empty-hint--padded">
              Записей пока нет.
            </div>
          ) : (
            <div className="mgr-journal">
              {daily.map((row) => (
                <JournalDay
                  key={row.id}
                  row={row}
                  expanded={expandedDays.has(row.id)}
                  onToggle={() => toggleJournalDay(row.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
